import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router";
import { RootState } from "../../../../../../app/store";
import { OptionsDetailBoardList } from "./OptionsDetailBoardList";
import { ShowTaskDetail } from "./ShowTaskDetail";
import { useGetAllProjectSections } from "@/features/projects/hooks/useGetAllProjectSections";
import { loadSections } from "@/features/projects/slices/sectionsSlice";
import { useCreateTask } from "@/features/projects/hooks/tasks/useCreateTask";
import { IFormData, ITask } from "@/api/tasks/types";
import { ISection } from "@/api/projects/types";
import { useGetAllTasksByProject } from "@/features/projects/hooks/tasks/useGetAllTasksByProject";
import { useUpdateTask } from "@/features/projects/hooks/tasks/useUpdateTask";
import { DragEndEvent, DragMoveEvent, DragStartEvent } from "@dnd-kit/core";
import { setDraggingTaskPositions } from "@/features/projects/slices/projectsSlice";
import { CircularLoader } from "@/components/Layout/CircularLoader";
import { useGetProjectById } from "@/features/projects/hooks/useGetProjectById";
import { toast } from "react-toastify";
import { arrayMove } from "@dnd-kit/sortable";
import { DetailProjectBoard } from "./boardView/DetailProjectBoard";
import { DetailProjectList } from "./listView/DetailProjectList";
import { TasksDebugInfo } from "@/components/Debug/TasksDebugInfo";

// Hook personalizado para actualizar el título de la página
const usePageTitle = (projectName: string, taskName?: string) => {
  useEffect(() => {
    if (taskName) {
      document.title = `${taskName} - ${projectName} | Kairos`;
    } else {
      document.title = `${projectName} | Kairos`;
    }
  }, [projectName, taskName]);
};

export interface IGroupedSection extends ISection {
  tasks: ITask[];
}

export const DetailProject = () => {
  const { projectId } = useParams();
  const [projectIdValue, setProjectIdValue] = useState("");
  const { data: projectData, isPending: isProjectPending } = useGetProjectById(Number(projectId));

  useEffect(() => {
    if (projectId) {
      setProjectIdValue(projectId);
    }
  }, [projectId]);

  if (isProjectPending || !projectIdValue) {
    return <CircularLoader />;
  }

  return <DetailProjectContent project={projectData.data} projectIdValue={projectIdValue} />;
};


interface DetailProjectContentProps {
  project: { id: number; name: string; };
  projectIdValue: string;
}

const DetailProjectContent = ({ project, projectIdValue }: DetailProjectContentProps) => {
  const { name } = project;
  const { taskId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { draggingTaskPositions } = useSelector((state: RootState) => state.projects);
  const [taskSelected, setTaskSelected] = useState<ITask | null>(null);
  const [listBoard, setlistBoard] = useState("list");
  const { 
    data: sections, 
    isSuccess: isSectionsSucces, 
    refetch: refetchSections, 
    isPending: isSectionsPending,
    isError: isSectionsError
  } = useGetAllProjectSections(Number(projectIdValue));
  
  const { 
    data: tasksData, 
    refetch: refetchTasks, 
    isSuccess: isTasksSucces, 
    isPending: isTasksPending,
    isError: isTasksError
  } = useGetAllTasksByProject({
    enabled: !!projectIdValue,
    projectId: projectIdValue,
  });
  const { mutateAsync: createTask } = useCreateTask();
  const { updateTaskHook, isPending: isUpdatingTask } = useUpdateTask();
  const { data: sectionsData } = sections || {};
  const [groupedSections, setGroupedSections] = useState<IGroupedSection[]>([]);
  const [activeTask, setActiveTask] = useState<ITask | null>(null);

  // Usar el hook para actualizar el título de la página
  usePageTitle(name, taskSelected?.title);

  const handleCreateNewTask = () => {
    const defaultSectionId = sectionsData?.[0]?.id?.toString();
    if (!defaultSectionId) {
      toast.error("No se pueden crear tareas porque no hay secciones en el proyecto.");
      return;
    }
    const newTaskTemplate: ITask = {
      id: `new-task-${Date.now()}`,
      title: "",
      description: "",
      state: "Pendiente",
      isBeingCreated: true,
      sectionId: defaultSectionId,
      projectId: projectIdValue,
      startDate: undefined,
      endDate: undefined,
      // order: 0,
      parentTaskId: '',
      createdAt: new Date().toISOString(),
    };
    setTaskSelected(newTaskTemplate);
  };

  useEffect(() => {
    if (sections && isSectionsSucces) {
      dispatch(loadSections(sections));
    }
  }, [sections, isSectionsSucces, dispatch]);

  const findTaskContainer = (taskId: string) => {
    return sectionsData?.find((section: ISection) =>
      tasksData?.data?.some((task: ITask) =>
        task.id === taskId && task.sectionId === section.id.toString()
      )
    )?.id?.toString();
  };

  const onDragStart = (event: DragStartEvent) => {
    const task = tasksData?.data?.find((t: ITask) => t.id === event.active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const onDragEnd = async (result: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = result;

    if (!over || !over.id || active.id === over.id) {
      dispatch(setDraggingTaskPositions({}));
      return;
    }

    const sourceTask = tasksData?.data?.find((task: ITask) => task.id === active.id);
    if (!sourceTask) return;

    const sourceSectionId = sourceTask.sectionId;
    let overSectionId: string | undefined;

    if (over.id.toString().startsWith('emptyRow_')) {
      overSectionId = over.id.toString().split('_')[1];
    } else {
      const overTask = tasksData?.data?.find((task: ITask) => task.id === over.id);
      overSectionId = overTask?.sectionId;
    }

    if (!sourceSectionId || !overSectionId) return;

    // Aplicar cambio optimista inmediatamente
    const updatedSections = groupedSections.map(section => {
      if (section.id.toString() === sourceSectionId) {
        // Remover la tarea de la sección origen
        return {
          ...section,
          tasks: section.tasks.filter(task => task.id !== active.id)
        };
      }
      if (section.id.toString() === overSectionId) {
        // Agregar la tarea a la sección destino
        const updatedTask = { ...sourceTask, sectionId: overSectionId };
        return {
          ...section,
          tasks: [...section.tasks, updatedTask]
        };
      }
      return section;
    });

    // Aplicar el cambio optimista inmediatamente
    setGroupedSections(updatedSections);

    if (sourceSectionId !== overSectionId) {
      try {
        await toast.promise(
          updateTaskHook({
            taskId: sourceTask.id,
            task: { ...sourceTask, sectionId: overSectionId }
          }),
          {
            pending: "Moviendo la tarea...",
            success: "Tarea movida exitosamente",
            error: "Error al mover la tarea"
          }
        );
        // Solo refetch si la actualización fue exitosa
        await Promise.all([refetchSections(), refetchTasks()]);
      } catch (error) {
        console.error("Error al mover la tarea:", error);
        toast.error("Error al mover la tarea");
        // Revertir el cambio optimista en caso de error
        await Promise.all([refetchSections(), refetchTasks()]);
      } finally {
        dispatch(setDraggingTaskPositions({}));
      }
      return;
    }

    if (sourceSectionId === overSectionId) {
      const sectionTasks = groupedSections.find(s => s.id.toString() === sourceSectionId)?.tasks || [];
      const oldIndex = sectionTasks.findIndex(t => t.id === active.id);
      const newIndex = sectionTasks.findIndex(t => t.id === over.id);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const reorderedTasks = arrayMove(sectionTasks, oldIndex, newIndex);

      const updatedSections = groupedSections.map(section => {
        if (section.id.toString() === sourceSectionId) {
          return { ...section, tasks: reorderedTasks };
        }
        return section;
      });

      setGroupedSections(updatedSections);
      console.log('Reordenado:', reorderedTasks.map(t => ({ id: t.id, title: t.title })));
      toast.info("Tarea reordenada (la persistencia en backend es un TODO).");
    }
  };

  const dragMoveFn = (event: DragMoveEvent) => {
    const { active, over } = event;

    if (!over?.id) return;

    const activeTaskId = active.id.toString();
    const overTaskId = over.id.toString();

    const sourceSection = findTaskContainer(activeTaskId);
    const targetSection = over.id.toString().startsWith('emptyRow_')
      ? over.id.toString().split('_')[1]
      : findTaskContainer(overTaskId);

    if (!sourceSection || !targetSection || sourceSection === targetSection) return;

    const newPositions = { ...draggingTaskPositions };

    const draggedTask = tasksData?.data?.find((task: ITask) => task.id === activeTaskId);
    if (draggedTask) {
      newPositions[sourceSection] = (newPositions[sourceSection] || [])
        .filter(task => task.id !== activeTaskId);

      newPositions[targetSection] = [
        ...(newPositions[targetSection] || []),
        draggedTask
      ];

      dispatch(setDraggingTaskPositions(newPositions));
    }
  };

  useEffect(() => {
    let grouped = [];
    if (!sections?.data || !tasksData?.data || !isSectionsSucces || !isTasksSucces) {
      return;
    }

    if (isSectionsSucces && isTasksSucces) {
      console.log('Sections data:', sections.data);
      console.log('Tasks data:', tasksData.data);
      
      grouped = sections?.data?.map((section: ISection) => {
        const sectionTasks = tasksData?.data?.filter((task: ITask) => {
          // Comparar tanto como string como número para mayor compatibilidad
          const taskSectionId = String(task.sectionId);
          const sectionId = String(section.id);
          console.log(`Comparing task.sectionId: ${taskSectionId} with section.id: ${sectionId}`);
          return taskSectionId === sectionId;
        }) || [];
        
        console.log(`Section ${section.name} (id: ${section.id}) has ${sectionTasks.length} tasks`);
        
        return {
          ...section,
          tasks: sectionTasks
        };
      });
    }

    console.log('Grouped sections:', grouped);
    setGroupedSections(grouped);
  }, [sections?.data, tasksData?.data, isSectionsSucces, isTasksSucces]);

  const createTaskHandler = async (form: IFormData): Promise<ITask> => {
    try {
      form.projectId = projectIdValue;
      const response = await toast.promise(createTask(form), {
        pending: "Creando la tarea...",
        success: "Tarea creada exitosamente",
        error: "Error al crear la tarea"
      });
      await refetchTasks();
      if (!response?.data) {
        throw new Error("No se pudo crear la tarea");
      }
      return response.data;
    } catch (error) {
      console.error("Error al crear la tarea:", error);
      throw error;
    }
  };

  const handleListBoard = (value: string) => {
    setlistBoard(value);
  };

  const selectTask = (objectTask: ITask) => {
    setTaskSelected(objectTask);
    // Actualizar la URL para incluir el taskId
    navigate(`/projects/${projectIdValue}/task/${objectTask.id}`);
  };

  const closeDetailProject = () => {
    setTaskSelected(null);
    // Navegar de vuelta al proyecto sin la tarea
    navigate(`/projects/${projectIdValue}`);
  };

  // Cargar la tarea desde la URL cuando se carga el componente
  useEffect(() => {
    if (taskId && tasksData?.data && isTasksSucces) {
      const taskFromUrl = tasksData.data.find((task: ITask) => task.id === taskId);
      if (taskFromUrl) {
        setTaskSelected(taskFromUrl);
      } else {
        // Si la tarea no existe, redirigir al proyecto
        navigate(`/projects/${projectIdValue}`);
        toast.error("La tarea especificada no existe");
      }
    }
  }, [taskId, tasksData?.data, isTasksSucces, projectIdValue, navigate]);

  const handleTaskUpdate = async () => {
    await Promise.all([refetchSections(), refetchTasks()]);
  };

  // Mostrar mensaje de error si ambas APIs fallan
  if (isSectionsError && isTasksError) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <h3 className="text-lg font-semibold mb-4">Error al cargar el proyecto</h3>
        <p className="text-gray-600 mb-4">
          No se pudieron cargar las secciones y tareas del proyecto. 
          Esto puede deberse a problemas de conectividad.
        </p>
        <button 
          onClick={() => {
            refetchSections();
            refetchTasks();
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Mostrar mensaje si solo las tareas fallan pero las secciones cargan
  if (isSectionsSucces && isTasksError) {
    toast.warn("No se pudieron cargar las tareas del proyecto", { toastId: 'tasks-error' });
  }

  return (
    <>
      <TasksDebugInfo projectId={projectIdValue} />
      <div className="flex flex-col gap-4 mx-auto w-full">
        <div className="flex justify-between items-center mb-4">
          <OptionsDetailBoardList
            handleListBoard={handleListBoard}
            projectName={name}
            idproject={projectIdValue}
            handleCreateNewTask={handleCreateNewTask}
            changeFilter={() => { }}
            filter=""
            newTask={createTaskHandler}
            sections={sections?.data || []}
            listBoard={listBoard}
          />
          {/* Botón temporal para testing con datos mock */}
          {projectIdValue === '136' && (
            <button
              onClick={() => {
                console.log('Forcing mock data reload...');
                refetchTasks();
              }}
              className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
              title="Usar datos mock para testing"
            >
              🔄 Mock Data
            </button>
          )}
        </div>
        <div className="w-full">
          {listBoard === "list" ? (
            <DetailProjectList
              groupedSections={groupedSections}
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              activeTask={activeTask}
              dragMoveFn={dragMoveFn}
              selectTask={selectTask}
              newTask={createTaskHandler}
              tasksData={tasksData?.data || []}
            />
          ) : (
            <DetailProjectBoard
              groupedSections={groupedSections}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              dragMoveFn={dragMoveFn}
              newTask={createTaskHandler}
              selectTask={selectTask}
              activeTask={activeTask}
            />
          )}
        </div>
      </div>
      {taskSelected && (
        <ShowTaskDetail
          showTask={taskSelected}
          closeDetailProject={closeDetailProject}
          idproject={projectIdValue}
          projectName={name}
          createTask={createTaskHandler}
          onTaskUpdate={handleTaskUpdate}
        />
      )}
      <div>
        {isUpdatingTask || isSectionsPending || isTasksPending ? <CircularLoader /> : null}
      </div>
    </>
  );
}
